<template>
  <div class="col-sm-12">
    <div class="card">
      <div class="card-body">

        <!-- Download Section -->
        <div class="dropdown-download-container">

          <div class="dropdown-item">
            <label for="monthYearPicker">Select Year &amp; Month</label>
            <input
              type="month"
              v-model="monthYear"
              class="search-input"
              :max="currentMonthYear"
              @change="handleMonthYearChange"
              id="monthYearPicker"
            />
          </div>

          <div class="dropdown-item">
            <label for="distributorSelect">Select Distributor</label>
            <select
              id="distributorSelect"
              v-model="selectedDistributorId"
              class="search-input"
              @change="handleDistributorChange"
              :disabled="loading1 || isDistributorLocked"
            >
              <option value="">-- Select Distributor --</option>
              <option
                v-for="dist in distributors"
                :key="dist.profile_id"
                :value="dist.profile_id"
              >
                {{ dist.name }}
              </option>
            </select>
          </div>

          <button
            class="download-button"
            @click="downloadExcel"
            :disabled="downloading || !monthYear || !selectedDistributorId"
          >
            <span v-if="downloading" class="spinner-border spinner-border-sm"></span>
            <span v-else>
              <i class="fa-solid fa-arrow-down"></i>
              Download Template
            </span>
          </button>

        </div>

        <!-- Upload Section -->
        <div class="file-submit-container">
          <input type="file" ref="fileInput" @change="handleFileUpload" accept=".xls,.xlsx" />

          <div class="submit-container">
            <button
              class="btn button-default"
              @click="handleSubmit"
              :disabled="submitting || !fileUploaded || !isFileValid"
            >
              <span v-if="submitting" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              <span v-else>Submit</span>
            </button>
          </div>
        </div>

        <!-- Preview Table -->
        <div v-if="tableData.length" class="table-preview-wrapper">
          <div class="table-preview-header">
            <span class="table-preview-title">
              <i class="fa-solid fa-table"></i>
              Preview — {{ tableData.length }} row(s)
            </span>
            <span class="table-preview-hint">
              <i class="fa-solid fa-arrows-left-right"></i>
              Scroll horizontally to see all columns
            </span>
          </div>
          <div class="table-scroll-container" style="overflow-y: visible; max-height: none;">
            <table class="preview-table">
              <thead>
                <tr>
                  <th v-for="(header, index) in headers" :key="index">{{ header }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, rowIndex) in tableData" :key="rowIndex">
                  <td v-for="(cell, cellIndex) in row" :key="cellIndex">{{ cell }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import {
  fetchDistributors,
  downloadLorryTargetMatrix,
  saveLorryTarget,
} from "@/services/dataService";
import { useToast } from "vue-toastification";

export default {
  data() {
    return {
      tableData: [],
      headers: [],
      variantIdRow: [],
      lorryIdRow: [],
      isFileValid: false,
      isDistributorLocked: false,
      distTargetMap: [],

      token:     localStorage.getItem("token_set"),
      companyId: localStorage.getItem("company_id"),
      profileId: localStorage.getItem("profile_id"),

      downloading:  false,
      fileUploaded: false,
      submitting:   false,

      distributors:          [],
      selectedDistributorId: "",
      loading1:              false,

      currentMonthYear: new Date().toISOString().slice(0, 7),
    };
  },

  created() {
    this.fetchDistributorsList();
  },

  setup() {
    const toast = useToast();
    return { toast, monthYear: "" };
  },

  methods: {

    // ─── Fetch distributors ───────────────────────────────────────────────────
    async fetchDistributorsList() {
      try {
        this.loading1 = true;
        const response = await fetchDistributors(this.profileId, this.token);
        this.distributors = response.data.data;

        if (this.distributors.length === 1) {
          const only = this.distributors[0];
          const onlyId = String(only.profile_id ?? only.id);

          if (onlyId === String(this.profileId)) {
            this.selectedDistributorId = onlyId;
            this.isDistributorLocked = true;
          } else {
            this.selectedDistributorId = "";
            this.isDistributorLocked = false;
          }
        } else {
          this.isDistributorLocked = false;
        }
      } catch (e) {
        console.error("fetchDistributors error", e);
      } finally {
        this.loading1 = false;
      }
    },

    // ─── Change handlers ──────────────────────────────────────────────────────
    handleMonthYearChange() {
      this.resetFileState();
    },

    handleDistributorChange() {
      this.resetFileState();
    },

    resetFileState() {
      this.tableData    = [];
      this.headers      = [];
      this.variantIdRow = [];
      this.lorryIdRow   = [];
      this.distTargetMap = {};
      this.isFileValid  = false;
      this.fileUploaded = false;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },

    // ─── Helper: column letter from 0-based index ─────────────────────────────
    colLetter(idx) {
      let letter = "", n = idx;
      while (n >= 0) {
        letter = String.fromCharCode((n % 26) + 65) + letter;
        n = Math.floor(n / 26) - 1;
      }
      return letter;
    },

    // ─── Download Excel template ──────────────────────────────────────────────
    async downloadExcel() {
      if (!this.monthYear) {
        this.toast.error("Please select a year and month first.");
        return;
      }
      if (!this.selectedDistributorId) {
        this.toast.error("Please select a distributor first.");
        return;
      }

      try {
        this.downloading = true;
        const [year, month] = this.monthYear.split("-").map(Number);

        // Fetch lorry-day matrix data AND distributor targets
        const matrixRes = await downloadLorryTargetMatrix(
          { year, month, distributor_id: this.selectedDistributorId },
          this.token
        );
        this.distTargetMap = {};
        matrixRes.data.data.items.forEach(item => {
          this.distTargetMap[item.variant_id] = Number(item.distributor_target);
        });

        if (matrixRes.data.status !== "success") {
          this.toast.error("Failed to generate template.");
          return;
        }

        const { lorries, items } = matrixRes.data.data;

        if (!lorries.length) {
          this.toast.error("No lorries found for this distributor.");
          return;
        }
        if (!items.length) {
          this.toast.error("No distributor targets found for this month and distributor.");
          return;
        }

        // Days in selected month
        const daysInMonth = new Date(year, month, 0).getDate();
        const dayNumbers  = Array.from({ length: daysInMonth }, (_, i) => i + 1);

        // Group items by type
        const typeGroups = [];
        const typeMap    = {};
        items.forEach(item => {
          if (!typeMap[item.type_id]) {
            typeMap[item.type_id] = { type_id: item.type_id, type_name: item.name, items: [] };
            typeGroups.push(typeMap[item.type_id]);
          }
          typeMap[item.type_id].items.push(item);
        });

        const DIST_TARGET_ROW = 1;
        const TYPE_ROW        = 2;
        const VARIANT_ROW     = 3;
        const ID_ROW          = 4;
        const META_ROW        = 5;
        const DATA_START_ROW  = 6;

        // Fixed columns: Lorry No | Day
        const FIXED_COL_COUNT = 2;

        const ws     = {};
        const merges = [];

        const setCell = (col, row, value, style = {}) => {
          const ref = `${this.colLetter(col)}${row}`;
          ws[ref] = { v: value, t: typeof value === "number" ? "n" : "s", s: style };
        };

        // Styles
        const sDistTarget  = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "C00000" } }, alignment: { horizontal: "center", vertical: "center" } };
        const sTypeHeader  = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "2F5496" } }, alignment: { horizontal: "center", vertical: "center" } };
        const sItemHeader  = { font: { bold: true }, fill: { fgColor: { rgb: "BDD7EE" } }, alignment: { horizontal: "center", vertical: "center", wrapText: true } };
        const sIdHeader    = { font: { italic: true, color: { rgb: "888888" }, sz: 9 }, fill: { fgColor: { rgb: "F2F2F2" } }, alignment: { horizontal: "center" } };
        const sFixedHeader = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "1F3864" } }, alignment: { horizontal: "center", vertical: "center" } };
        const sLorryCell   = { font: { bold: true }, fill: { fgColor: { rgb: "DEEAF1" } }, alignment: { horizontal: "center", vertical: "center" } };
        const sDayCell     = { alignment: { horizontal: "center" } };

        // ── Fixed column headers ──
        ["Lorry No", "Day"].forEach((header, i) => {
          [DIST_TARGET_ROW, TYPE_ROW, VARIANT_ROW, ID_ROW].forEach(r => setCell(i, r, "", sFixedHeader));
          setCell(i, DIST_TARGET_ROW, header, sFixedHeader);
          merges.push({ s: { r: DIST_TARGET_ROW - 1, c: i }, e: { r: ID_ROW - 1, c: i } });
        });

        setCell(0, META_ROW, Number(this.selectedDistributorId), sIdHeader);
        setCell(1, META_ROW, year, sIdHeader);
        setCell(2, META_ROW, month, sIdHeader);

        // ── Item columns ──
        let colIndex = FIXED_COL_COUNT;
        typeGroups.forEach(group => {
          const startCol = colIndex;
          group.items.forEach(item => {
            // Row 1: distributor target for this variant
            const distTarget = this.distTargetMap[item.variant_id] ?? "";
            setCell(colIndex, DIST_TARGET_ROW, distTarget !== "" ? Number(distTarget) : "", sDistTarget);
            // Row 3: variant name
            setCell(colIndex, VARIANT_ROW, item.variant_name, sItemHeader);
            // Row 4: variant id (hidden reference)
            setCell(colIndex, ID_ROW, item.variant_id, sIdHeader);
            colIndex++;
          });
          const endCol = colIndex - 1;
          // Row 2: type group name
          setCell(startCol, TYPE_ROW, group.type_name, sTypeHeader);
          if (startCol < endCol) {
            for (let c = startCol + 1; c <= endCol; c++) setCell(c, TYPE_ROW, "", sTypeHeader);
            merges.push({ s: { r: TYPE_ROW - 1, c: startCol }, e: { r: TYPE_ROW - 1, c: endCol } });
          }
        });

        const TOTAL_COLS = colIndex;

        // ── Data rows: lorry × day ──
        let currentRow = DATA_START_ROW;
        lorries.forEach(lorry => {
          const lorryStartRow = currentRow;

          dayNumbers.forEach((day, dayIdx) => {
            // Col 0: Lorry No (only on first day row, rest blank for merge)
            setCell(0, currentRow, dayIdx === 0 ? lorry.vehicle_no : "", dayIdx === 0 ? sLorryCell : {});
            // Col 1: Day number
            setCell(1, currentRow, day, sDayCell);
            // Item columns: empty (user fills in)
            for (let c = FIXED_COL_COUNT; c < TOTAL_COLS; c++) {
              setCell(c, currentRow, "");
            }
            currentRow++;
          });

          // Merge Lorry No cell vertically across all day rows
          if (daysInMonth > 1) {
            merges.push({
              s: { r: lorryStartRow - 1, c: 0 },
              e: { r: currentRow - 2,    c: 0 },
            });
          }
        });

        const totalRows = currentRow - 1;
        ws["!ref"]    = `A1:${this.colLetter(TOTAL_COLS - 1)}${totalRows}`;
        ws["!merges"] = merges;

        // Column widths
        const colWidths = [{ wch: 18 }, { wch: 8 }];
        items.forEach(item => colWidths.push({ wch: Math.max(item.variant_name.length + 2, 14) }));
        ws["!cols"] = colWidths;

        // Row heights
        const rowHeights = [
          { hpt: 20 },
          { hpt: 22 },
          { hpt: 30 },
          { hpt: 0, hidden: true },
          { hpt: 0, hidden: true },
        ];
        ws["!rows"] = rowHeights;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, ws, "Lorry Target");
        XLSX.writeFile(workbook, `lorry_target_${year}_${month}_dist${this.selectedDistributorId}.xlsx`);

      } catch (error) {
        console.error(error);
        this.toast.error("Error downloading file.");
      } finally {
        this.downloading = false;
      }
    },

    // ─── Handle file upload & validation ─────────────────────────────────────
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.tableData    = [];
      this.headers      = [];
      this.variantIdRow = [];
      this.lorryIdRow   = [];
      this.isFileValid  = false;
      this.fileUploaded = false;

      if (!this.monthYear) {
        this.toast.error("Please select a year and month before uploading.");
        if (this.$refs.fileInput) this.$refs.fileInput.value = "";
        return;
      }
      if (!this.selectedDistributorId) {
        this.toast.error("Please select a distributor before uploading.");
        if (this.$refs.fileInput) this.$refs.fileInput.value = "";
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const data     = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet    = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

        const variantNameRow = jsonData[2] || [];
        const variantIdRow   = jsonData[3] || [];
        const metaRow        = jsonData[4] || [];
        const dataRows       = jsonData.slice(5);

        const FIXED_COL_COUNT = 2;
        const [selectedYear, selectedMonth] = this.monthYear.split("-").map(Number);

        const fileDistributorId = parseInt(metaRow[0]);
        const fileYear          = parseInt(metaRow[1]);
        const fileMonth         = parseInt(metaRow[2]);

        if (fileDistributorId !== Number(this.selectedDistributorId) ||
            fileYear  !== selectedYear ||
            fileMonth !== selectedMonth) {
          this.toast.error(
            `Wrong file: this file is for a different distributor or period. ` +
            `Please download the correct template.`
          );
          if (this.$refs.fileInput) this.$refs.fileInput.value = "";
          return;
        }

        // ── Build column maps ──
        const columnNameMap = {};
        variantNameRow.forEach((name, idx) => {
          if (idx >= FIXED_COL_COUNT && name !== "") columnNameMap[idx] = name;
        });

        const variantIdMap = {};
        variantIdRow.forEach((cell, idx) => {
          if (idx >= FIXED_COL_COUNT && cell !== "" && cell !== null) {
            const parsed = parseInt(String(cell));
            if (!isNaN(parsed)) variantIdMap[idx] = parsed;
          }
        });

        const distTargetRow = jsonData[0] || [];
        const distTargetMap = {};
        variantIdRow.forEach((cell, idx) => {
          if (idx >= FIXED_COL_COUNT && cell !== "" && cell !== null) {
            const variantId = parseInt(String(cell));
            const target    = distTargetRow[idx];
            if (!isNaN(variantId) && target !== "" && target !== null && target !== undefined) {
              distTargetMap[variantId] = Number(target);
            }
          }
        });

        const errors = [];

        // ── Check 1: Symbol / format validation ──
        dataRows.forEach((row, rowIdx) => {
          const displayRow = rowIdx + 1;
          Object.keys(columnNameMap).forEach(colIdxStr => {
            const colIdx    = parseInt(colIdxStr);
            const cellValue = row[colIdx];
            const colName   = columnNameMap[colIdx];
            if (cellValue === "" || cellValue === null || cellValue === undefined) return;
            const strValue = String(cellValue).trim();
            if (/[a-zA-Z]/.test(strValue)) {
              errors.push(`Row ${displayRow}, "${colName}": Letters are not allowed. Found "${strValue}".`);
            } else if (/[^0-9.]/.test(strValue)) {
              errors.push(`Row ${displayRow}, "${colName}": Special characters are not allowed. Found "${strValue}".`);
            } else if (strValue.includes(".")) {
              errors.push(`Row ${displayRow}, "${colName}": Decimals are not allowed. Found "${strValue}". Enter a whole number.`);
            }
          });
        });

        // ── Check 2: Grand total per item must not exceed distributor target ──
        if (errors.length === 0) {
          console.log("hi")
          const variantSums = {};
          dataRows.forEach(row => {
            Object.entries(variantIdMap).forEach(([colIdxStr, variantId]) => {
              const colIdx    = parseInt(colIdxStr);
              const cellValue = row[colIdx];
              if (cellValue === "" || cellValue === null || cellValue === undefined) return;
              variantSums[variantId] = (variantSums[variantId] || 0) + Number(cellValue);
            });
          });

          Object.entries(variantIdMap).forEach(([colIdxStr, variantId]) => {
            const colIdx   = parseInt(colIdxStr);
            const colName  = columnNameMap[colIdx] || `Col ${colIdx}`;
            const actual   = variantSums[variantId] || 0;
            const expected = distTargetMap[variantId];
            console.log(actual);
            console.log(expected);
            if (expected === undefined) return;
            if (actual > expected) {
              errors.push(
                `"${colName}": Total lorry daily targets (${actual}) exceed the distributor target (${expected}).`
              );
            } else if (actual < expected) {
              errors.push(
                `"${colName}": Total lorry daily targets (${actual}) not complete the distributor target (${expected}).`
              );
            }
          });
        }

        // ── Build preview ──
        this.headers      = ["Lorry No", "Day", ...Object.values(columnNameMap)];
        this.variantIdRow = variantIdRow;
        this.tableData    = dataRows;
        this.fileUploaded = true;
        if (this.$refs.fileInput) this.$refs.fileInput.value = "";

        if (errors.length > 0) {
          const errorText = errors.map((e, i) => `${i + 1}. ${e}`).join("\n\n");
          this.toast.error(`${errors.length} validation error(s) found:\n\n${errorText}`, {
            timeout: false,
            closeOnClick: false,
          });
          this.isFileValid = false;
        } else {
          this.toast.success("File validated successfully. Ready to submit.");
          this.isFileValid = true;
        }
      };

      reader.readAsArrayBuffer(file);
    },

    // ─── Submit ───────────────────────────────────────────────────────────────
    async handleSubmit() {
      try {
        this.submitting = true;

        if (!this.fileUploaded || !this.isFileValid) {
          this.toast.error("Please upload a valid file before submitting.");
          return;
        }

        const [year, month] = this.monthYear.split("-").map(Number);

        if (!year || !month) {
          this.toast.error("Please select a valid year and month.");
          return;
        }

        const FIXED_COL_COUNT = 2;

        // Build variantIdMap from stored variantIdRow
        const variantIdMap = {};
        this.variantIdRow.forEach((cell, colIdx) => {
          if (colIdx >= FIXED_COL_COUNT && cell !== "" && cell !== null && cell !== undefined) {
            const parsed = parseInt(String(cell));
            if (!isNaN(parsed)) variantIdMap[colIdx] = parsed;
          }
        });

        if (Object.keys(variantIdMap).length === 0) {
          this.toast.error("Invalid file format. Variant ID reference row not found.");
          return;
        }

        // Build payload
        const excelPayload = [];
        let currentLorryNo = null;

        this.tableData.forEach(row => {
          const rawLorry = row[0];
          if (rawLorry !== "" && rawLorry !== null && rawLorry !== undefined) {
            currentLorryNo = String(rawLorry).trim();
          }
          const day = parseInt(row[1]);
          if (!currentLorryNo || isNaN(day)) return;

          Object.entries(variantIdMap).forEach(([colIdxStr, variantId]) => {
            const colIdx      = parseInt(colIdxStr);
            const targetValue = row[colIdx];
            const numericTarget = (targetValue === null || targetValue === undefined || targetValue === "") 
              ? 0 
              : Number(targetValue);

            excelPayload.push({
              distributor_profile_id: Number(this.selectedDistributorId),
              vehicle_no:  currentLorryNo,
              year,
              month,
              day,
              variant_id: variantId,
              target: numericTarget,
            });
          });
        });

        if (excelPayload.length === 0) {
          this.toast.error("No target data found in the uploaded file.");
          return;
        }

        const finalPayload = {
          target_created_by:      this.profileId,
          distributor_profile_id: Number(this.selectedDistributorId),
          year,
          month,
          Excel: excelPayload,
        };

        const response = await saveLorryTarget(finalPayload, this.token);

        if (response.status === 200) {
          this.toast.success("Lorry targets saved successfully!");
          this.resetFileState();
        } else if (response.status === 422) {
          console.error("Validation errors:", response.data.errors);
          this.toast.error("Validation failed. Please check the file and try again.");
        } else {
          console.error("Server error:", response.data.error);
          this.toast.error("Server error. All changes were rolled back.");
        }

      } catch (e) {
        console.error("Error processing lorry target upload:", e);
        this.toast.error("Something went wrong. Please try again.");
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
  
<style scoped>
.card {
  padding: 24px;
}

/* ── Download bar ── */
.dropdown-download-container {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.dropdown-item {
  flex: 1;
  min-width: 180px;
  max-width: 450px;
}

label {
  font-weight: bold;
  font-size: 14px;
}

.search-input {
  flex: 1;
  padding: 10px;
  margin-left: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
}

.search-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

.download-button {
  margin-left: auto;
  background-color: #1D6F42;
  color: white;
  border: none;
  border-radius: 5px;
  padding: 8px 16px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 14px;
}

.download-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ── Upload bar ── */
.file-submit-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 16px;
  padding: 12px 16px;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
}

/* ── Preview table wrapper ── */
.table-preview-wrapper {
  margin-top: 20px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
}

.table-preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background-color: #f1f3f5;
  border-bottom: 1px solid #dee2e6;
}

.table-preview-title {
  font-weight: 600;
  font-size: 13px;
  color: #343a40;
  display: flex;
  align-items: center;
  gap: 6px;
}

.table-preview-hint {
  font-size: 11px;
  color: #868e96;
  display: flex;
  align-items: center;
  gap: 4px;
}

.table-scroll-container {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 450px;
  width: 100%;
}

/* ── Preview table ── */
.preview-table {
  border-collapse: collapse;
  white-space: nowrap;
  min-width: 100%;
  font-size: 13px;
}

.preview-table thead tr {
  position: sticky;
  top: 0;
  z-index: 2;
}

.preview-table th {
  padding: 10px 14px;
  background-color: #343a40;
  color: #ffffff;
  font-weight: 600;
  text-align: center;
  border: 1px solid #495057;
  white-space: nowrap;
}

.preview-table td {
  padding: 8px 14px;
  border: 1px solid #dee2e6;
  text-align: center;
  color: #212529;
  min-width: 120px;
}

.preview-table tbody tr:nth-child(even) {
  background-color: #f8f9fa;
}

.preview-table tbody tr:hover {
  background-color: #e8f4fd;
  transition: background-color 0.15s ease;
}

/* ── Toast line breaks ── */
:deep(.Vue-Toastification__toast-body) {
  white-space: pre-line;
}
</style>